/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Match Booster offerings", description = "Learn about the audience boosting, conversion event boosting, anonymous visitor boosting, and CTV device boosting offerings.", lastUpdatedDate = new Date(1778803200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Match Booster offerings",
        description: "Learn about the audience boosting, conversion event boosting, anonymous visitor boosting, and CTV device boosting offerings.",
        lastUpdatedDate: "Fri May 15 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Audience boosting",
          "depth": 0,
          "id": "audience-boosting"
        }, {
          "value": "Conversion event boosting",
          "depth": 0,
          "id": "conversion-event-boosting"
        }, {
          "value": "Anonymous visitor boosting",
          "depth": 0,
          "id": "anonymous-visitor-boosting"
        }, {
          "value": "CTV device boosting",
          "depth": 0,
          "id": "ctv-device-boosting"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Match Booster",
        extraText: " as an add-on product."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, data teams"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Match Booster includes four offerings that address different activation scenarios: audience boosting, conversion event boosting, anonymous visitor boosting, and CTV device boosting."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "audience-boosting",
        children: "Audience boosting"
      }), "\n", _jsx(_components.p, {
        children: "Audience boosting matches more of your customer records to users across ad platforms, including walled gardens, DSPs, SSPs, CTV platforms, and publisher-direct destinations."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can match audiences across consumers, B2B employees, households, and devices."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Use cases:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Custom targeting"
          }), " — Help ad platforms match more of your audience so campaigns reach a larger share of the segment."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Suppression"
          }), " — Improve suppression lists so ad platforms can exclude more existing customers from acquisition campaigns."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Lookalikes"
          }), " — Give ad platforms a larger matched seed audience to build more representative lookalike models."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Bid optimization"
          }), " — Help ad platforms distinguish new versus returning customers for bid strategy adjustments."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Offer optimization"
          }), " — Match more of the qualified audience for special offers, pricing, or discounts, such as loyalty members who qualify for discounted rates on search platforms."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "conversion-event-boosting",
        children: "Conversion event boosting"
      }), "\n", _jsx(_components.p, {
        children: "Conversion event boosting helps ad platforms match more of the online and offline conversion events you send, so more conversions are attributed to ad interactions."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Conversion event boosting does not cause the same event to be sent to the ad\nplatform multiple times."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Use cases:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Attribution and measurement"
          }), " — Match more conversions to ad impressions and clicks for more accurate attribution and incrementality measurement."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Budget allocation"
          }), " — When more conversions are attributed, ad platforms can make better budget allocation decisions across ads, ad groups, and campaigns."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Conversion optimization"
          }), " — Send more complete conversion data to the ad platform's bidding and targeting systems."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "anonymous-visitor-boosting",
        children: "Anonymous visitor boosting"
      }), "\n", _jsx(_components.p, {
        children: "Anonymous visitor boosting resolves anonymous visitors via IP addresses. You may be collecting user events and their associated IP addresses, even when the visitor does not provide personally identifiable information like email or phone. Hightouch's anonymous identity graph uses reverse IP lookup to identify emails and device IDs associated with an IP address, then sends those identifiers to ad platforms for retargeting."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Hightouch rigorously cleans observed linkages between IP addresses, hashed\nemails, and MAIDs to eliminate public locations."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Use cases:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Web retargeting"
          }), " — Retarget anonymous visitors to your web properties by resolving their IP addresses to matchable identifiers."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "ctv-device-boosting",
        children: "CTV device boosting"
      }), "\n", _jsx(_components.p, {
        children: "CTV device boosting targets consumers on connected TV devices like Roku, LG, Samsung, and Vizio. Most connected TV platforms rely on CTV IDs and IP address matching to target viewers with ads. Hightouch's identity graph translates your first-party identifiers into CTV IDs and IP addresses that these platforms use for matching."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Hightouch rigorously cleans observed linkages between IP addresses, hashed\nemails, and MAIDs to eliminate public locations."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Use cases:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User acquisition"
          }), " — Acquire new app installs and subscribers through personalized CTV ads and more effective suppression."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Subscription upsell"
          }), " — Upsell customers from a free to paid tier on your CTV application."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Tune-in"
          }), " — Drive consumers to your app for specific shows, movies, or live events by displaying native ads on smart TV home screens."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
